import { useMemo } from 'react'
import { DollarSign } from 'lucide-react'
import { NumericInput } from '@/components/ui/NumericInput'
import { PlanEditorCalculatedField } from '@/components/Deals/PlanEditorCalculatedField'
import type { ComboVariation, ComboVariationPricing, ComboVariationWithPricing } from '@/types'
import { inferComboPricingModel, resolveComboVariationPricing } from '@/utils/comboVariationPricing'

const brl = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

type Props = {
  variation: ComboVariation
  priced: ComboVariationWithPricing
  onChange: (patch: Partial<ComboVariation>) => void
}

function mergePricing(
  current: ComboVariationPricing | undefined,
  patch: Partial<ComboVariationPricing>,
): ComboVariationPricing {
  return { ...current, ...patch }
}

export function ComboVariationPricingFields({ variation, priced, onChange }: Props) {
  const pricingModel = variation.pricingModel ?? inferComboPricingModel(priced)
  const pricing = variation.pricing ?? {}

  const resolved = useMemo(() => resolveComboVariationPricing(priced), [priced])

  const installmentValue = pricing.installmentValue ?? resolved.installmentValue
  const entry = pricing.entry ?? resolved.entry
  const installments = pricing.installments ?? resolved.installments
  const total = pricing.total ?? resolved.total

  const patchPricing = (patch: Partial<ComboVariationPricing>) => {
    const next = mergePricing(variation.pricing ?? pricing, patch)
    if (pricingModel === 'recorrente') {
      const mensal = Number(next.installmentValue ?? 0)
      const meses = Number(next.installments ?? installments)
      const entrada = Number(next.entry ?? 0)
      next.total = entrada + mensal * meses + (priced.valorSetup ?? 0)
      next.recorrente = true
    } else {
      const projeto = Number(next.total ?? total)
      const entrada = Number(next.entry ?? 0)
      const parcelas = Number(next.installments ?? installments)
      next.installmentValue =
        parcelas > 0 ? Math.max(0, (projeto - entrada) / parcelas) : projeto
      next.recorrente = false
    }
    onChange({ pricing: next })
  }

  return (
    <div className='space-y-3 rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] p-3'>
      <div className='flex items-center justify-between gap-2 border-b border-white/[0.06] pb-2'>
        <h4 className='flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-accent'>
          <DollarSign size={14} />
          Precificação do combo (contrato / Anexo 1)
        </h4>
        <div className='flex rounded-lg border border-white/[0.06] bg-white/[0.04] p-0.5'>
          <button
            type='button'
            onClick={() => onChange({ pricingModel: 'recorrente', pricing: { ...pricing, recorrente: true } })}
            className={`rounded-md px-2 py-1 text-[10px] font-semibold ${
              pricingModel === 'recorrente' ? 'bg-accent text-white' : 'text-text-muted'
            }`}
          >
            Recorrente
          </button>
          <button
            type='button'
            onClick={() => onChange({ pricingModel: 'setup', pricing: { ...pricing, recorrente: false } })}
            className={`rounded-md px-2 py-1 text-[10px] font-semibold ${
              pricingModel === 'setup' ? 'bg-accent text-white' : 'text-text-muted'
            }`}
          >
            Projeto
          </button>
        </div>
      </div>

      {pricingModel === 'recorrente' ? (
        <div className='grid grid-cols-1 gap-3 sm:grid-cols-4'>
          <NumericInput
            label='Mensalidade (BRL)'
            step={0.01}
            min={0}
            emptyFallback={0}
            value={installmentValue}
            onValueChange={(v) => patchPricing({ installmentValue: v })}
          />
          <NumericInput
            label='Entrada / Setup (BRL)'
            step={0.01}
            min={0}
            emptyFallback={0}
            value={entry}
            onValueChange={(v) => patchPricing({ entry: v })}
            hint='Opcional'
          />
          <NumericInput
            label='Fidelidade (meses)'
            min={1}
            emptyFallback={1}
            value={installments}
            onValueChange={(v) => patchPricing({ installments: v })}
          />
          <PlanEditorCalculatedField label='Valor total' value={brl(total)} />
        </div>
      ) : (
        <div className='grid grid-cols-1 gap-3 sm:grid-cols-4'>
          <NumericInput
            label='Valor do projeto (BRL)'
            step={0.01}
            min={0}
            emptyFallback={0}
            value={total}
            onValueChange={(v) => patchPricing({ total: v })}
          />
          <NumericInput
            label='Entrada (BRL)'
            step={0.01}
            min={0}
            emptyFallback={0}
            value={entry}
            onValueChange={(v) => patchPricing({ entry: v })}
          />
          <NumericInput
            label='Qtd. parcelas'
            min={1}
            emptyFallback={1}
            value={installments}
            onValueChange={(v) => patchPricing({ installments: v })}
          />
          <PlanEditorCalculatedField
            label='Valor da parcela'
            value={brl(installmentValue)}
            valueClassName='text-amber-400'
          />
        </div>
      )}

      {(priced.valorSetup ?? 0) > 0 && pricingModel === 'recorrente' ? (
        <p className='text-[10px] text-text-muted'>
          Setup avulso dos itens: {brl(priced.valorSetup)} (incluído no total quando informado).
        </p>
      ) : null}
    </div>
  )
}
